<div class="flex max-h-[90vh] min-h-0 w-full flex-col">
  <!-- Header -->
  <div
    class="flex shrink-0 flex-col gap-2 border-b border-gray-200 px-4 py-3 dark:border-gray-700 sm:flex-row sm:items-center sm:justify-between"
  >
    <div class="min-w-0">
      <h2 class="truncate text-lg font-semibold">{{ 'routing.SUB_ROUTE_PARTNERS.DIALOGS.BULK_GPS.HEADER_TITLE' | t }}</h2>
      <p class="truncate text-sm text-gray-500">
        {{ 'routing.SUB_ROUTE_PARTNERS.DIALOGS.BULK_GPS.HEADER_ROUTE' | t: { name: dialogData.subRouteName } }}
      </p>
    </div>
    <button mat-icon-button type="button" (click)="close()" [attr.aria-label]="'GENERAL.CLOSE' | t">
      <mat-icon>close</mat-icon>
    </button>
  </div>

  <!-- Body -->
  <div class="min-h-0 flex-1 overflow-y-auto p-4">
    @switch (step()) {
      @case (BulkGpsUpdateStep.CONFIGURE) {
        <!-- GPS configuration header (same pattern as business partner modal) -->
        <app-form-section
          [title]="'routing.SUB_ROUTE_PARTNERS.DIALOGS.BULK_GPS.SECTION_TITLE' | t"
          icon="gps_fixed"
          iconColor="blue"
          class="mb-4"
          contentClass="flex flex-col gap-4"
        >
          <div class="rounded-lg border border-gray-200 bg-default p-4">
            <app-toggle-option
              [formControl]="gpsForm.controls.useGps"
              icon="gps_fixed"
              iconColor="blue"
              [title]="'routing.SUB_ROUTE_PARTNERS.DIALOGS.BULK_GPS.TOGGLE_TITLE' | t"
              [description]="'routing.SUB_ROUTE_PARTNERS.DIALOGS.BULK_GPS.TOGGLE_DESC' | t"
            />

            @if (gpsForm.controls.useGps.value) {
              <div
                class="mt-4 grid grid-cols-1 gap-4 border-t border-gray-200 pt-4 sm:grid-cols-2"
              >
                <mat-form-field class="w-full">
                  <mat-label>{{ 'routing.SUB_ROUTE_PARTNERS.DIALOGS.BULK_GPS.MODE_LABEL' | t }}</mat-label>
                  <mat-select [formControl]="gpsForm.controls.useGpsMode">
                    @for (mode of useGpsModeOptions; track mode.value) {
                      <mat-option [value]="mode.value">{{ getUseGpsModeSelectLabel(mode.value) }}</mat-option>
                    }
                  </mat-select>
                </mat-form-field>

                <mat-form-field class="w-full">
                  <mat-label>{{ 'routing.SUB_ROUTE_PARTNERS.DIALOGS.BULK_GPS.DISTANCE_LABEL' | t }}</mat-label>
                  <input
                    matInput
                    type="number"
                    min="0"
                    [formControl]="gpsForm.controls.distanceBtwSellerAndCustomer"
                  />
                  <span matSuffix>km</span>
                </mat-form-field>
              </div>
            }
          </div>
        </app-form-section>

        <!-- Client selection -->
        <div class="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 class="text-sm font-semibold uppercase tracking-wide text-gray-700">
              {{ 'routing.SUB_ROUTE_PARTNERS.DIALOGS.BULK_GPS.SELECT_CLIENTS_TITLE' | t }}
            </h3>
            <p class="text-sm text-gray-500">
              {{
                'routing.SUB_ROUTE_PARTNERS.DIALOGS.BULK_GPS.SELECTED_OF_TOTAL'
                  | t: { count: selectedCount(), total: totalItems(), max: maxPartnersPerRequest }
              }}
            </p>
          </div>
          <mat-form-field class="w-full sm:max-w-xs" subscriptSizing="dynamic">
            <mat-icon matPrefix>search</mat-icon>
            <input
              matInput
              [placeholder]="'routing.SUB_ROUTE_PARTNERS.DIALOGS.BULK_GPS.SEARCH_PLACEHOLDER' | t"
              [value]="searchTerm()"
              (input)="onSearchChange($any($event.target).value)"
            />
          </mat-form-field>
        </div>

        <app-smart-grid
          [config]="gridConfig"
          [columnDefs]="colDefs"
          [data]="partners()"
          [totalRecords]="totalItems()"
          [loading]="loading()"
          [pageIndex]="pageIndex()"
          [initialSelectedRows]="selectedPartners()"
          (selectionChanged)="onSelectionChange($event)"
          (pageChanged)="onPageChange($event)"
          (sortChanged)="onSortChange($event)"
        />
      }

      @case (BulkGpsUpdateStep.CONFIRM) {
        <div class="mx-auto flex w-full max-w-lg flex-col gap-4">
          <div class="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
            <mat-icon class="shrink-0 text-amber-600">info</mat-icon>
            <div class="min-w-0 text-sm">
              <p class="font-semibold text-gray-800">{{ 'routing.SUB_ROUTE_PARTNERS.DIALOGS.BULK_GPS.CONFIRM_TITLE' | t }}</p>
              <p class="mt-1 text-gray-600" [innerHTML]="'routing.SUB_ROUTE_PARTNERS.DIALOGS.BULK_GPS.CONFIRM_MESSAGE' | t: { count: confirmSummary().selectedCount }"></p>
            </div>
          </div>

          <div class="rounded-lg border border-gray-200 bg-default p-4 text-sm">
            <dl class="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <dt class="text-gray-500">{{ 'routing.SUB_ROUTE_PARTNERS.DIALOGS.BULK_GPS.SUMMARY_CLIENTS_LABEL' | t }}</dt>
                <dd class="font-medium">{{ confirmSummary().selectedCount }}</dd>
              </div>
              <div>
                <dt class="text-gray-500">{{ 'routing.SUB_ROUTE_PARTNERS.DIALOGS.BULK_GPS.SUMMARY_GPS_VALIDATION_LABEL' | t }}</dt>
                <dd class="font-medium">
                  {{
                    confirmSummary().useGps
                      ? ('routing.SUB_ROUTE_PARTNERS.PERMISSION_GPS.CHIP_ACTIVE' | t)
                      : ('routing.SUB_ROUTE_PARTNERS.PERMISSION_GPS.CHIP_INACTIVE' | t)
                  }}
                </dd>
              </div>
              @if (confirmSummary().useGps) {
                <div>
                  <dt class="text-gray-500">{{ 'routing.SUB_ROUTE_PARTNERS.DIALOGS.BULK_GPS.SUMMARY_MODE_LABEL' | t }}</dt>
                  <dd class="font-medium">{{ confirmSummary().useGpsModeLabel }}</dd>
                </div>
                <div>
                  <dt class="text-gray-500">{{ 'routing.SUB_ROUTE_PARTNERS.DIALOGS.BULK_GPS.SUMMARY_DISTANCE_LABEL' | t }}</dt>
                  <dd class="font-medium">{{ confirmSummary().distance }} km</dd>
                </div>
              }
            </dl>
          </div>
        </div>
      }

      @case (BulkGpsUpdateStep.RESULTS) {
        @if (result(); as res) {
          <div class="mx-auto flex w-full max-w-2xl flex-col gap-4">
            <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div class="rounded-lg border border-gray-200 bg-default p-4 text-center">
                <p class="text-2xl font-bold text-gray-800">{{ res.total }}</p>
                <p class="text-xs text-gray-500 uppercase">{{ 'routing.SUB_ROUTE_PARTNERS.DIALOGS.BULK_GPS.RESULTS_TOTAL' | t }}</p>
              </div>
              <div class="rounded-lg border border-green-200 bg-green-50 p-4 text-center">
                <p class="text-2xl font-bold text-green-700">{{ res.successCount }}</p>
                <p class="text-xs text-green-700 uppercase">{{ 'routing.SUB_ROUTE_PARTNERS.DIALOGS.BULK_GPS.RESULTS_SUCCESS' | t }}</p>
              </div>
              <div class="rounded-lg border border-red-200 bg-red-50 p-4 text-center">
                <p class="text-2xl font-bold text-red-700">{{ res.failedCount }}</p>
                <p class="text-xs text-red-700 uppercase">{{ 'routing.SUB_ROUTE_PARTNERS.DIALOGS.BULK_GPS.RESULTS_FAILED' | t }}</p>
              </div>
            </div>

            @if (res.items.length > 0) {
              <div class="overflow-x-auto rounded-lg border border-gray-200">
                <table class="w-full min-w-[320px] text-left text-sm">
                  <thead class="border-b border-gray-200 bg-gray-50 text-xs uppercase text-gray-500">
                    <tr>
                      <th class="px-3 py-2">{{ 'routing.SUB_ROUTE_PARTNERS.DIALOGS.BULK_GPS.RESULTS_COL_CLIENT' | t }}</th>
                      <th class="px-3 py-2">{{ 'GENERAL.STATUS' | t }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    @for (item of res.items; track item.businessPartnerId) {
                      <tr class="border-b border-gray-100 last:border-0">
                        <td class="px-3 py-2">
                          <span class="font-medium">{{ item.code }}</span>
                          <span class="text-gray-600"> — {{ item.name }}</span>
                        </td>
                        <td class="px-3 py-2">
                          @if (item.success) {
                            <span class="inline-flex items-center gap-1 text-green-700">
                              <mat-icon class="!h-4 !w-4 !text-base">check_circle</mat-icon>
                              {{ 'routing.SUB_ROUTE_PARTNERS.DIALOGS.BULK_GPS.RESULTS_UPDATED' | t }}
                            </span>
                          } @else {
                            <span class="inline-flex items-center gap-1 text-red-700">
                              <mat-icon class="!h-4 !w-4 !text-base">error</mat-icon>
                              {{ item.message ?? ('routing.SUB_ROUTE_PARTNERS.DIALOGS.BULK_GPS.RESULTS_ERROR_FALLBACK' | t) }}
                            </span>
                          }
                        </td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>
            }
          </div>
        }
      }
    }
  </div>

  <!-- Footer -->
  <div
    class="flex shrink-0 flex-col-reverse gap-2 border-t border-gray-200 px-4 py-3 dark:border-gray-700 sm:flex-row sm:items-center sm:justify-end"
  >
    @switch (step()) {
      @case (BulkGpsUpdateStep.CONFIGURE) {
        <button mat-button type="button" (click)="close()">{{ 'GENERAL.CANCEL' | t }}</button>
        <button
          mat-flat-button
          color="primary"
          type="button"
          [disabled]="selectedCount() === 0"
          (click)="goToConfirm()"
        >
          {{ 'routing.SUB_ROUTE_PARTNERS.DIALOGS.BULK_GPS.BTN_CONTINUE' | t }}
        </button>
      }
      @case (BulkGpsUpdateStep.CONFIRM) {
        <button mat-button type="button" (click)="backToConfigure()" [disabled]="submitting()">
          {{ 'CONFIRM.DISCARD.CANCEL' | t }}
        </button>
        <button
          mat-flat-button
          color="primary"
          type="button"
          [disabled]="submitting()"
          (click)="confirmUpdate()"
        >
          @if (submitting()) {
            <mat-spinner diameter="20" class="mr-2 inline-block"></mat-spinner>
          }
          {{ 'routing.SUB_ROUTE_PARTNERS.DIALOGS.BULK_GPS.BTN_CONFIRM_UPDATE' | t }}
        </button>
      }
      @case (BulkGpsUpdateStep.RESULTS) {
        <button mat-flat-button color="primary" type="button" (click)="close(true)">
          {{ 'GENERAL.CLOSE' | t }}
        </button>
      }
    }
  </div>
</div>
